import React, { useEffect, useRef, useState } from "react";
import mqtt from "mqtt";
import { fetchBrokerIp } from "./brokerIp";

function useGamepad() {
  const raf = useRef(null);
  const [pads, setPads] = useState({});

  const read = () => {
    const gps = navigator.getGamepads ? navigator.getGamepads() : [];
    const next = {};
    for (const g of gps) {
      if (!g) continue;
      next[g.index] = {
        id: g.id,
        index: g.index,
        buttons: g.buttons.map(b => ({ pressed: b.pressed, value: b.value })),
        axes: g.axes.slice(),
      };
    }
    setPads(next);
    raf.current = requestAnimationFrame(read);
  };

  useEffect(() => {
    const onConnect = () => {
      if (!raf.current) raf.current = requestAnimationFrame(read);
    };
    const onDisconnect = () => {
      if (raf.current) cancelAnimationFrame(raf.current);
      raf.current = null;
      setPads({});
    };

    window.addEventListener("gamepadconnected", onConnect);
    window.addEventListener("gamepaddisconnected", onDisconnect);

    return () => {
      window.removeEventListener("gamepadconnected", onConnect);
      window.removeEventListener("gamepaddisconnected", onDisconnect);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, []);

  return pads;
}
/**
 * Empaqueta un eje en un byte (8 bits) con el siguiente layout:
 * bit 7 (MSB): 1 si es negativo, 0 si es positivo/0
 * bits 0..6: magnitud en [0..100] redondeada (|v| * 100), saturada a 127
 * v esperado en [-1, 1] (Gamepad API)
 */
function packAxis(v) {
  if (Number.isNaN(v)) v = 0;
  const sign = v < 0 ? 1 : 0; // 1 = negativo
  let mag = Math.round(Math.min(1, Math.abs(v)) * 100); // 0..100
  if (mag > 127) mag = 127; // seguridad
  return (sign << 7) | (mag & 0x7f);
}

/**
 * Empaqueta botones en un byte con el siguiente mapeo (LSB = bit1):
 * bit1 -> botón 0
 * bit2 -> botón 6
 * bit3 -> botón 7
 * bit4..bit7 -> botones 12..15
 * bit8 (MSB) -> botón 17
 */
const BUTTON_BIT = {
  BTN0: 0,
  BTN6: 1,
  BTN7: 2,
  BTN4: 3,
  BTN5: 4,
  BTN12: 5,
  BTN13: 6,
  BTN14: 7,
  BTN15: 8,
  BTN17: 9,
};

const BUTTON_MASK_ALL = Object.values(BUTTON_BIT).reduce((acc, bit) => acc | (1 << bit), 0);

function packButtons(buttons = []) {
  const isDown = (i) => Boolean(buttons[i]?.pressed);
  let mask = 0;
  if (isDown(0)) mask |= 1 << BUTTON_BIT.BTN0;
  if (isDown(6)) mask |= 1 << BUTTON_BIT.BTN6;
  if (isDown(7)) mask |= 1 << BUTTON_BIT.BTN7;
  if (isDown(4)) mask |= 1 << BUTTON_BIT.BTN4;
  if (isDown(5)) mask |= 1 << BUTTON_BIT.BTN5;
  if (isDown(12)) mask |= 1 << BUTTON_BIT.BTN12;
  if (isDown(13)) mask |= 1 << BUTTON_BIT.BTN13;
  if (isDown(14)) mask |= 1 << BUTTON_BIT.BTN14;
  if (isDown(15)) mask |= 1 << BUTTON_BIT.BTN15;
  if (isDown(17)) mask |= 1 << BUTTON_BIT.BTN17;
  return mask & BUTTON_MASK_ALL;
}

/**
 * Devuelve 6 bytes codificando:
 *  [0] = ejeX stick 1 (axes[0])
 *  [1] = ejeY stick 1 (axes[1])
 *  [2] = ejeX stick 2 (axes[2])
 *  [3] = ejeY stick 2 (axes[3])
 *  [4..5] = botones según packButtons()
 *
 * Nota: Asumo este reparto (1-2 primer stick, 3-4 segundo stick) para
 * cuadrar con tu descripción; si prefieres otra asignación, lo cambiamos.
 */
function encodePadPayload(pad) {
  if (!pad) return new Uint8Array([0, 0, 0, 0, 0, 0]);
  const a = pad.axes || [];
  const bytes = new Uint8Array(6);
  bytes[0] = packAxis(a[0] || 0);
  bytes[1] = packAxis(a[1] || 0);
  bytes[2] = packAxis(a[2] || 0);
  bytes[3] = packAxis(a[3] || 0);
  const mask = packButtons(pad.buttons || []);
  bytes[4] = mask & 0xff;
  bytes[5] = (mask >> 8) & 0xff;
  return bytes;
}

function toHex(byte) {
  return "0x" + byte.toString(16).padStart(2, "0");
}
const MQTT_PORT = 9001;
const MQTT_PORT_SEC = 9002;
const BROKER_FALLBACK = "81.41.185.133";

export default function GamepadDemo() {
  const pads = useGamepad();
  const anyPad = Object.values(pads)[0];
  const padsRef = useRef(pads);
  const mqttClientRef = useRef(null);
  const [mqttStatus, setMqttStatus] = useState("Resolviendo broker...");
  const [frequency, setFrequency] = useState(25); // Hz, default 25 Hz
  const [messageCount, setMessageCount] = useState(0);
  const [commandLog, setCommandLog] = useState([]);
  const [brokerHost, setBrokerHost] = useState(null);

  // Actualizar ref cada vez que pads cambia
  useEffect(() => {
    padsRef.current = pads;
  }, [pads]);

  // Función para añadir comando al log
  const addCommandToLog = (command) => {
    const timestamp = new Date().toLocaleTimeString();
    setCommandLog(prev => [{time: timestamp, command}, ...prev].slice(0, 5)); // Mantener solo los últimos 5
  };

  useEffect(() => {
    let mounted = true;
    fetchBrokerIp(BROKER_FALLBACK).then(ip => {
      if (mounted) {
        setBrokerHost(ip);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Conexión MQTT
  useEffect(() => {
    if (!brokerHost) return;
    console.log("Intentando conectar a MQTT...");
    setMqttStatus("Conectando...");
    const url = `ws://${brokerHost}:${MQTT_PORT}`;
    //const url = `wss://${brokerHost}:${MQTT_PORT_SEC}`;
    const client = mqtt.connect(url, {
      username: "supervisor",
      password: "1234",
      clientId: `gamepad_${Math.random().toString(16).slice(2, 10)}`,
      reconnectPeriod: 1000
    });

    client.on("connect", () => {
      console.log("✅ MQTT conectado exitosamente", url);
      setMqttStatus("Conectado");
    });

    client.on("error", (err) => {
      console.error("❌ MQTT error:", err);
      setMqttStatus("Error: " + err.message);
    });

    client.on("offline", () => {
      console.log("⚠️ MQTT desconectado");
      setMqttStatus("Desconectado");
    });

    mqttClientRef.current = client;

    return () => {
      if (client) {
        console.log("Cerrando conexión MQTT");
        client.end();
      }
      mqttClientRef.current = null;
    };
  }, [brokerHost]);

  // Envío de datos por MQTT
  useEffect(() => {
    const intervalMs = 1000 / frequency; // Convertir Hz a milisegundos
    console.log(`🔍 [DEBUG] Iniciando intervalo de publicación MQTT a ${frequency} Hz (${intervalMs.toFixed(1)}ms)`);

    const interval = setInterval(() => {
      // Leer del ref para obtener el valor actual
      const currentPad = Object.values(padsRef.current)[0];

      if (currentPad && mqttClientRef.current && mqttClientRef.current.connected) {
        const pkt = encodePadPayload(currentPad);

        // Verificar comandos
        const btn0 = currentPad.buttons[0]?.pressed;  // X
        const btn6 = currentPad.buttons[6]?.pressed;  // L2
        const btn7 = currentPad.buttons[7]?.pressed;  // R2
        const btn4 = currentPad.buttons[4]?.pressed;  // L1
        const btn5 = currentPad.buttons[5]?.pressed;  // R1

        if (btn0 && btn7) {
          console.log("🔫 [ARM] X+R2 presionados - Enviando comando ARM");
          addCommandToLog("🔫 ARM");
        } else if (btn0 && btn6) {
          console.log("🛑 [DISARM] X+L2 presionados - Enviando comando DISARM");
          addCommandToLog("🛑 DISARM");
        } else if (btn7 && !btn0) {
          console.log("🎮 [AUTO] Solo R2 presionado - Modo AUTO");
          addCommandToLog("🎮 Modo AUTO");
        } else if (btn6 && !btn0) {
          console.log("🎮 [LOITER] Solo L2 presionado - Modo LOITER");
          addCommandToLog("🎮 Modo LOITER");
        } else if (btn5) {
          console.log("🏠 [RTL] R1 presionado - Modo RTL");
          addCommandToLog("🏠 Modo RTL");
        } else if (btn4) {
          console.log("🎮 [LOITER] L1 presionado - Modo LOITER");
          addCommandToLog("🎮 Modo LOITER");
        }

        console.log("🔍 [DEBUG] Paquete:", Array.from(pkt));

        mqttClientRef.current.publish(
          "/MQ10/drones/SCOUT_INDI/joystick/bytes",
          pkt,
          { qos: 0 },
          (err) => {
            if (err) {
              console.error("❌ Error publicando MQTT:", err);
            } else {
              console.log("✅ [DEBUG] Publicado exitosamente");
              setMessageCount(prev => prev + 1);
            }
          }
        );
      }
    }, intervalMs);

    return () => {
      console.log("🔍 [DEBUG] Limpiando intervalo");
      clearInterval(interval);
    };
  }, [frequency]); // Se reinicia cuando cambia la frecuencia


  return (
    <div style={{ fontFamily: "sans-serif", padding: "1rem" }}>
      <h1>Control de Multirrotor</h1>

      {/* Status MQTT y Estadísticas */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
        <div style={{
          padding: "0.5rem",
          background: mqttStatus === "Conectado" ? "#d4edda" : "#f8d7da",
          border: `1px solid ${mqttStatus === "Conectado" ? "#c3e6cb" : "#f5c6cb"}`,
          borderRadius: "4px"
        }}>
          <strong>MQTT:</strong> {mqttStatus}
        </div>
        <div style={{
          padding: "0.5rem",
          background: "#e7f3ff",
          border: "1px solid #b3d9ff",
          borderRadius: "4px"
        }}>
          <strong>Mensajes enviados:</strong> {messageCount}
        </div>
      </div>

      {/* Log de Comandos */}
      <div style={{
        padding: "1rem",
        marginBottom: "1rem",
        background: "#f8f9fa",
        border: "1px solid #dee2e6",
        borderRadius: "4px"
      }}>
        <strong style={{ display: "block", marginBottom: "0.5rem" }}>Últimos Comandos:</strong>
        {commandLog.length === 0 ? (
          <div style={{ fontSize: "0.9rem", color: "#6c757d" }}>No hay comandos recientes</div>
        ) : (
          <div style={{ fontSize: "0.9rem" }}>
            {commandLog.map((entry, idx) => (
              <div key={idx} style={{ padding: "0.25rem 0", borderBottom: idx < commandLog.length - 1 ? "1px solid #dee2e6" : "none" }}>
                <span style={{ color: "#6c757d", marginRight: "0.5rem" }}>[{entry.time}]</span>
                <span>{entry.command}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Control de Frecuencia */}
      <div style={{
        padding: "1rem",
        marginBottom: "1rem",
        background: "#fff3cd",
        border: "1px solid #ffc107",
        borderRadius: "4px"
      }}>
        <div style={{ marginBottom: "0.5rem" }}>
          <strong>Frecuencia de Envío: {frequency} Hz</strong> ({(1000 / frequency).toFixed(1)} ms)
        </div>
        <input
          type="range"
          min="1"
          max="50"
          value={frequency}
          onChange={(e) => setFrequency(Number(e.target.value))}
          style={{ width: "100%" }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem", color: "#666" }}>
          <span>1 Hz</span>
          <span>50 Hz</span>
        </div>
      </div>

      {/* Instrucciones de control */}
      <div style={{
        padding: "1rem",
        marginBottom: "1rem",
        background: "#e7f3ff",
        border: "1px solid #b3d9ff",
        borderRadius: "4px"
      }}>
        <h3 style={{ marginTop: 0 }}>Controles del Dron</h3>
        <ul style={{ marginBottom: 0 }}>
          <li><strong>Botón 0 (X) + Botón 7 (R2/Gatillo Derecho)</strong>: ARM</li>
          <li><strong>Botón 0 (X) + Botón 6 (L2/Gatillo Izquierdo)</strong>: DISARM</li>
          <li><strong>Solo Botón 7 (R2/Gatillo Derecho)</strong>: Modo AUTO</li>
          <li><strong>Solo Botón 6 (L2/Gatillo Izquierdo)</strong>: Modo LOITER (mantiene posición GPS + altura)</li>
          <li><strong>Solo Botón 5 (R1/Bumper Derecho)</strong>: Modo RTL (Return To Launch)</li>
          <li><strong>Solo Botón 4 (L1/Bumper Izquierdo)</strong>: Modo LOITER</li>
          <li><strong>Stick Izquierdo</strong>: Throttle (Y) + Yaw (X)</li>
          <li><strong>Stick Derecho</strong>: Pitch (Y) + Roll (X)</li>
          <li><strong>Botones 12-15</strong>: Control Gimbal (tilt/pan)</li>
        </ul>
      </div>

      {Object.values(pads).length === 0 ? (
        <div>Conecta un mando y pulsa un botón.</div>
      ) : (
        Object.values(pads).map(p => (
          <div key={p.index} style={{ marginBottom: "1rem", border: "1px solid #ccc", padding: "0.5rem" }}>
            <strong>{p.id}</strong>

            <div>Botones presionados: {p.buttons.filter(b => b.pressed).length}</div>

            {/* Stick izquierdo */}
            <div>Stick Izq → X {p.axes[0]?.toFixed(2)} | Y {p.axes[1]?.toFixed(2)}</div>

            {/* Stick derecho */}
            <div>Stick Der → X {p.axes[2]?.toFixed(2)} | Y {p.axes[3]?.toFixed(2)}</div>

            <div style={{ display: "flex", flexWrap: "wrap", marginTop: "8px" }}>
              {p.buttons.map((b, i) => (
                <div
                  key={i}
                  style={{
                    width: "40px",
                    height: "40px",
                    margin: "2px",
                    background: b.pressed ? "black" : "#eee",
                    color: b.pressed ? "white" : "black",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.8rem",
                    borderRadius: "6px"
                  }}
                >
                  {i}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
